"use client";

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Crown, Calendar, Users, Book, Palette, Star, Eye } from 'lucide-react';
import { AboutLayout } from '@/components/about/about-layout';

export default function RiderWaiteTarotPage() {
  return (
    <AboutLayout activeSection="tarot">
      <div className="max-w-4xl mx-auto px-4 py-8">
            {/* Current Section Indicator */}
            <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
              <div className="flex items-center gap-2 text-yellow-300">
                <Crown className="h-4 w-4" />
                <span className="text-sm font-medium">🃏 Tarot</span>
                <span className="text-yellow-500">/</span>
                <span className="text-sm">Sistemler</span>
                <span className="text-yellow-500">/</span>
                <span className="text-sm">Rider-Waite-Smith</span>
              </div>
            </div>

            {/* Breadcrumb */}
            <div className="mb-6">
              <Link
                href="/about"
                className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                AXTROLOG Nedir
              </Link>
              <span className="text-slate-500 mx-2">/</span>
              <Link
                href="/about/tarot/systems"
                className="text-slate-400 hover:text-slate-200 transition-colors"
              >
                Tarot Sistemleri
              </Link>
              <span className="text-slate-500 mx-2">/</span>
              <span className="text-slate-300">Rider-Waite-Smith</span>
            </div>

            {/* Header */}
            <div className="bg-gradient-to-r from-yellow-900/20 to-amber-900/20 rounded-xl p-8 mb-8 border border-yellow-500/20">
              <h1 className="text-4xl font-bold text-slate-100 mb-4 flex items-center gap-3">
                <Crown className="h-10 w-10 text-yellow-400" />
                Rider-Waite-Smith (RWS) Tarot
              </h1>
              <p className="text-lg text-slate-200 leading-relaxed">
                1910'da yayımlanmış en yaygın kullanılan modern tarot sistemi. Arthur Edward Waite'nin
                konsepti ve Pamela Colman Smith'in sanatsal yorumu ile yaratılmış klasik deste.
              </p>
            </div>

            {/* Historical Background */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Tarihsel Geçmiş</h2>
              <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-yellow-400" />
                      Yayım Tarihleri
                    </h3>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                        <span className="text-sm text-slate-400">Aralık 1909: İlk sınırlı baskı</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                        <span className="text-sm text-slate-400">Mart 1910: Geniş kapsamlı yayım</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                        <span className="text-sm text-slate-400">William Rider & Son, Londra</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                      <Users className="h-5 w-5 text-yellow-400" />
                      Yaratıcılar
                    </h3>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                        <span className="text-sm text-slate-400">Arthur Edward Waite (Konsept)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                        <span className="text-sm text-slate-400">Pamela Colman Smith (İllüstrasyon)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                        <span className="text-sm text-slate-400">Her ikisi de Golden Dawn üyesi</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Deck Structure */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Deste Yapısı</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Major Arcana */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-700 rounded-lg flex items-center justify-center">
                      <Star className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-100">Majör Arkana (22 Kart)</h3>
                  </div>
                  <p className="text-slate-300 mb-4">
                    Büyük sırları temsil eden kartlar. Geleneksel tarot sıralamasından farklı olarak
                    Strength (Güç) ve Justice (Adalet) kartlarının sırası değiştirilmiş.
                  </p>
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">0-XXI numaralı kartlar</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">Astrolojik correspondence'larla uyumlu</span>
                    </div>
                  </div>
                  <Link
                    href="/about/tarot/systems/rider-waite/major-arcana"
                    className="inline-flex items-center px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors text-sm"
                  >
                    <Star className="h-4 w-4 mr-2" />
                    22 Majör Arkana Kartını Gör
                  </Link>
                </div>

                {/* Minor Arcana */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-700 rounded-lg flex items-center justify-center">
                      <Book className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-100">Minör Arkana (56 Kart)</h3>
                  </div>
                  <p className="text-slate-300 mb-4">
                    Dört suit'ten oluşan günlük yaşam kartları. RWS'nin en büyük yeniliği
                    tüm pip kartlarında detaylı görsel anlatımlar sunması.
                  </p>
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-emerald-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">Wands, Cups, Swords, Pentacles</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-emerald-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">Her suit'te Ace-10 + 4 court kartı</span>
                    </div>
                  </div>
                  <Link
                    href="/about/tarot/systems/rider-waite/minor-arcana"
                    className="inline-flex items-center px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors text-sm"
                  >
                    <Book className="h-4 w-4 mr-2" />
                    56 Minör Arkana Kartını Gör
                  </Link>
                </div>
              </div>
            </div>

            {/* Key Innovations */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Önemli Yenilikler</h2>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-700 rounded-lg flex items-center justify-center">
                      <Palette className="h-5 w-5 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">Görsel Sembolizm</h3>
                  </div>
                  <p className="text-slate-300 text-sm">
                    İlk kez tüm 78 kartta detaylı allegorik illüstrasyonlar.
                    Özellikle Minör Arkana pip kartları hikaye anlatır.
                  </p>
                </div>

                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-700 rounded-lg flex items-center justify-center">
                      <Eye className="h-5 w-5 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">Ezoterik Sentez</h3>
                  </div>
                  <p className="text-slate-300 text-sm">
                    Golden Dawn öğretileri, Kabala, astroloji ve alchemy
                    sembollerini görsel bir dile çevirme.
                  </p>
                </div>

                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-700 rounded-lg flex items-center justify-center">
                      <Users className="h-5 w-5 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">Erişilebilirlik</h3>
                  </div>
                  <p className="text-slate-300 text-sm">
                    Beginner-friendly yaklaşım. Kartların kendilerinden
                    anlam çıkarılabilir, kapsamlı hafıza gerektirmez.
                  </p>
                </div>
              </div>
            </div>

            {/* Example Cards */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Örnek Kartlar</h2>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* The Fool */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 hover:border-yellow-500/50 transition-all duration-300 group">
                  <div className="flex flex-col items-center mb-4">
                    <div className="w-32 h-48 mb-4 rounded-lg overflow-hidden group-hover:scale-105 transition-transform duration-300">
                      <img src="/images/tarot/cards/rider-waite-fool.svg" alt="The Fool" className="w-full h-full object-cover" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">The Fool (0)</h3>
                    <p className="text-yellow-400 text-sm mb-3">Yeni başlangıçlar</p>
                  </div>
                  <p className="text-slate-300 text-sm leading-relaxed mb-4">
                    Yolculuğun başlangıcını temsil eder. Masumiyet, spontanlık ve sınırsız potansiyel.
                    Uçurumun kenarında duran figür cesaretle ileriye adım atar.
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                      <span className="text-xs text-slate-400">Beyaz gül: Saflık</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                      <span className="text-xs text-slate-400">Küçük köpek: Sadakat</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                      <span className="text-xs text-slate-400">Çanta: Bilinçsiz potansiyel</span>
                    </div>
                  </div>
                </div>

                {/* The Magician */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 hover:border-yellow-500/50 transition-all duration-300 group">
                  <div className="flex flex-col items-center mb-4">
                    <div className="w-32 h-48 mb-4 rounded-lg overflow-hidden group-hover:scale-105 transition-transform duration-300">
                      <img src="/images/tarot/cards/rider-waite-magician.svg" alt="The Magician" className="w-full h-full object-cover" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">The Magician (I)</h3>
                    <p className="text-yellow-400 text-sm mb-3">İrade ve manifestasyon</p>
                  </div>
                  <p className="text-slate-300 text-sm leading-relaxed mb-4">
                    Yaratıcı gücün ve manifestasyonun kartı. Dört elementin kontrolü altında olan figür,
                    "Yukarıdaki gibi aşağıda da" prensibini temsil eder.
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                      <span className="text-xs text-slate-400">Sonsuzluk sembolü: Ruhsal güç</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                      <span className="text-xs text-slate-400">Dört element: Tam control</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                      <span className="text-xs text-slate-400">Güller ve zambaklar: Tutku ve saflık</span>
                    </div>
                  </div>
                </div>

                {/* The High Priestess */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 hover:border-yellow-500/50 transition-all duration-300 group">
                  <div className="flex flex-col items-center mb-4">
                    <div className="w-32 h-48 mb-4 rounded-lg overflow-hidden group-hover:scale-105 transition-transform duration-300">
                      <img src="/images/tarot/cards/rider-waite-high-priestess.svg" alt="The High Priestess" className="w-full h-full object-cover" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">The High Priestess (II)</h3>
                    <p className="text-yellow-400 text-sm mb-3">Sezgi ve gizli bilgi</p>
                  </div>
                  <p className="text-slate-300 text-sm leading-relaxed mb-4">
                    Bilinçsiz bilgelik ve sezginin kartı. İki sütun arasında oturan figür,
                    görünen ve görünmeyen dünyalar arasındaki köprüyü temsil eder.
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                      <span className="text-xs text-slate-400">B ve J sütunları: İkili zıtlık</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                      <span className="text-xs text-slate-400">TORA: Gizli öğretiler</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                      <span className="text-xs text-slate-400">Hilal ay: Kadınsı enerji</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Legacy and Influence */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Miras ve Etki</h2>
              <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-100 mb-3">Dünya Çapında Etki</h3>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                        <span className="text-sm text-slate-400">100 milyondan fazla kopyası dolaşımda</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                        <span className="text-sm text-slate-400">Yüzlerce variant ve imitasyon yarattı</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                        <span className="text-sm text-slate-400">Modern tarot öğretiminin standardı</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-slate-100 mb-3">Telif Hakkı Durumu</h3>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                        <span className="text-sm text-slate-400">1966'da ABD'de kamu malı oldu</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                        <span className="text-sm text-slate-400">70 yıl kuralının olduğu ülkelerde serbest</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                        <span className="text-sm text-slate-400">Serbest kullanım ve modifikasyona açık</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Christian Imagery Changes */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Görsel Değişiklikler</h2>
              <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <p className="text-slate-300 mb-4">
                  RWS, önceki tarot geleneklerinden farklı olarak Hristiyani imgeleri kaldırdı veya
                  modifiye etti, bunun yerine daha evrensel ezoterik semboller kullandı.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div className="p-4 bg-slate-700 rounded-lg">
                    <h4 className="font-semibold text-slate-200 mb-2">Kaldırılan/Değiştirilen:</h4>
                    <ul className="text-sm text-slate-400 space-y-1">
                      <li>• Açık Hristiyani semboller</li>
                      <li>• Papa ve Papessa kartları modifiye edildi</li>
                      <li>• Dini figürler nötrleştirildi</li>
                    </ul>
                  </div>

                  <div className="p-4 bg-slate-700 rounded-lg">
                    <h4 className="font-semibold text-slate-200 mb-2">Eklenen:</h4>
                    <ul className="text-sm text-slate-400 space-y-1">
                      <li>• Éliphas Lévi'nin ezoterik sembolleri</li>
                      <li>• Golden Dawn correspondence'ları</li>
                      <li>• Kabalistik ve astrolojik referanslar</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Important Note */}
            <div className="bg-amber-900/20 border border-amber-700/50 rounded-xl p-6 mb-8">
              <h3 className="text-lg font-semibold text-amber-200 mb-3">⚠️ Önemli Uyarı</h3>
              <p className="text-amber-100 leading-relaxed">
                Rider-Waite-Smith tarot sistemi sembolik yorumlama aracıdır ve kesinlik iddiası taşımaz.
                İçerikler tamamen merak, eğlence ve kişisel farkındalık amaçlıdır. Yaşamsal kararlar için
                uzman görüşü alınmalıdır.
              </p>
            </div>

            {/* Main Tarot Training CTA */}
            <div className="bg-gradient-to-r from-purple-900/30 to-pink-900/30 border border-purple-500/30 rounded-xl p-6 mb-8">
              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                    <Users className="h-8 w-8 text-white" />
                  </div>
                </div>
                <h3 className="text-2xl font-semibold text-slate-100 mb-3">🃏 Tarot Eğitimleri</h3>
                <p className="text-slate-300 mb-6 leading-relaxed max-w-2xl mx-auto">
                  Tarot kart sistemleri, sembolik yorumlama teknikleri, kart dizilimleri ve okuma yöntemleri hakkında
                  detaylı eğitim programları için kayıt olun. Merak ettiğiniz konularda rehberlik alın.
                </p>
                <button className="inline-flex items-center px-8 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold rounded-lg transition-all transform hover:scale-105 shadow-lg">
                  <Users className="h-5 w-5 mr-2" />
                  Tarot Eğitimi İçin Kayıt Ol
                </button>
                <p className="text-slate-400 text-sm mt-3">
                  * Eğitim programları sadece bilgilendirme ve kişisel gelişim amaçlıdır
                </p>
              </div>
            </div>

            {/* Navigation */}
            <div className="flex justify-between items-center">
              <Link
                href="/about/tarot/systems"
                className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Tarot Sistemleri
              </Link>

              <Link
                href="/about/tarot/systems/marseille"
                className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                Tarot de Marseille
                <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
              </Link>
            </div>
      </div>
    </AboutLayout>
  );
}